import { Trans, useLingui } from "@lingui/react/macro";
import { providerFullDisplayName } from "@pontimed/types";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AlertCircle } from "lucide-react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import { WidgetGenerator } from "@/components/account/widget-generator";
import { Skeleton } from "@/components/ui/skeleton";

interface ProviderBasic {
  id: string;
  firstName: string;
  lastName: string;
  profession?: string | null;
  primaryCountry: string | null;
}

interface ProviderResponse {
  provider: ProviderBasic;
}

const providerBasicQueryOptions = {
  queryKey: ["api", "/api/account/provider", "basic"] as const,
  queryFn: () => apiFetch.get<ProviderResponse>("/api/account/provider"),
  retry: (n: number, err: unknown) =>
    !(err instanceof ApiError && (err.status === 401 || err.status === 403)) && n < 2,
};

export const Route = createFileRoute("/_authenticated/account/provider/booking-widget")({
  loader: ({ context }) => context.queryClient.ensureQueryData(providerBasicQueryOptions),
  component: ProviderBookingWidgetPage,
});

function ProviderBookingWidgetPage() {
  const { i18n } = useLingui();
  const query = useQuery(providerBasicQueryOptions);

  if (query.isLoading) {
    return (
      <div className="mx-auto max-w-4xl space-y-4 p-4 sm:p-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-48 w-full rounded-xl" />
        <Skeleton className="h-32 w-full rounded-xl" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="mx-auto max-w-4xl p-4 sm:p-6">
        <div className="border-destructive/40 bg-destructive/5 rounded-xl border p-6 text-center">
          <AlertCircle className="text-destructive mx-auto size-8" />
          <p className="text-destructive mt-3 text-sm font-medium">
            <Trans>Could not load provider profile.</Trans>
          </p>
        </div>
      </div>
    );
  }

  const { provider } = query.data;

  return (
    <>
      <WidgetGenerator
        providerId={provider.id}
        providerName={providerFullDisplayName(provider, i18n.locale)}
        country={(provider.primaryCountry || "ro").toLowerCase()}
        currentLocale={i18n.locale}
      />
      <AccountSecurityBanner />
    </>
  );
}
